/** Canonical utilities for sending WebSocket messages: the two transport primitives and the typed senders over them.
 *  The FRAME each typed sender composes lives on the const floor (`const/ws-frames.ts`) since 2026-09-02, when this leaf
 *  sat at its UTILS_MAX_LINES utils cap (`QUAL-FILE-SIZE-CAP-HEADROOM-TIGHT-001`) -- the frames are pure over their inputs and the
 *  UserDO broadcast path builds the same DATA_RESPONSE frame for a different transport, so the shapes belong below both. */

import { WS_ACTIONS } from '@federa/const/websocket-actions';
import { buildWsActionResponseFrame, buildWsDataResponseFrame, buildWsErrorFrame, buildWsSuccessFrame } from '@federa/const/ws-frames';
import type {
	SendWsActionResponseOptions,
	SendWsDataResponseOptions,
	SendWsErrorOptions,
	SendWsSuccessOptions,
	WsDataResponsePayload,
	WsOutboundFrame,
} from '@federa/types/api-websocket';

export function sendWsMessage(ws: WebSocket, message: WsOutboundFrame): boolean {
	// Intentional: serialization errors return false -- caller handles gracefully. Delegates the frame dispatch (and its ws.send() catch) to sendStringifiedWsMessage.
	try {
		return sendStringifiedWsMessage(ws, JSON.stringify(message));
	} catch {
		return false;
	}
}

/** Send a pre-stringified WebSocket frame. Used by broadcast helpers that serialize a payload once and fan out to many sockets. */
export function sendStringifiedWsMessage(ws: WebSocket, frame: string): boolean {
	try {
		ws.send(frame);
		return true;
	} catch {
		return false;
	}
}

export function sendWsError(ws: WebSocket, message: string, options: SendWsErrorOptions = {}): boolean {
	return sendWsMessage(ws, buildWsErrorFrame(message, options));
}

export function sendWsSuccess(ws: WebSocket, options: SendWsSuccessOptions): boolean {
	return sendWsMessage(ws, buildWsSuccessFrame(options));
}

export function sendWsActionResponse(ws: WebSocket, options: SendWsActionResponseOptions): boolean {
	return sendWsMessage(ws, buildWsActionResponseFrame(options));
}

/** Send a data response under a caller-supplied action; without a dataType the data rides in `message`. */
export function sendWsDataResponse(ws: WebSocket, options: SendWsDataResponseOptions): boolean {
	const { action, dataType, data } = options;
	return sendWsMessage(ws, dataType ? { action, dataType, data } : { action, message: data });
}

/** Send a data response under the canonical DATA_RESPONSE action. */
export function sendWsDataResponseAction(ws: WebSocket, payload: WsDataResponsePayload): boolean {
	return sendWsMessage(ws, buildWsDataResponseFrame(payload));
}

/** Send the canonical pong frame. The caller ensures readiness; false means the underlying send dropped it (backpressure). */
export function respondPong(ws: WebSocket): boolean {
	return sendWsMessage(ws, { action: WS_ACTIONS.PONG, timestamp: Date.now() });
}
